// Audio playback store
import { create } from 'zustand';
import { AudioFile } from '@types';

interface AudioStore {
  currentFile: AudioFile | null;
  currentlyPlaying: string | null;
  volume: number;
  isMuted: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  audioUrl: string | null;
  
  // Actions
  setCurrentFile: (file: AudioFile | null) => void;
  setCurrentlyPlaying: (fileId: string | null) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setAudioUrl: (url: string | null) => void;
  play: (file: AudioFile) => void;
  stop: () => void;
  /** Optional callback when playback ends (e.g. for A/B loop). Cleared on stop(). */
  setOnPlaybackEnded: (cb: (() => void) | null) => void;
  /** Called by MiniPlayer when audio ends. */
  _notifyPlaybackEnded: () => void;
}

let playbackEndedCallback: (() => void) | null = null;

export const useAudioStore = create<AudioStore>((set, get) => ({
  currentFile: null,
  currentlyPlaying: null,
  volume: 0.7,
  isMuted: false,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  audioUrl: null,

  setCurrentFile: (file: AudioFile | null) => {
    set({ currentFile: file, currentlyPlaying: file?.id || null });
  },

  setCurrentlyPlaying: (fileId: string | null) => {
    set({ currentlyPlaying: fileId });
  },

  setVolume: (volume: number) => {
    set({ volume: Math.max(0, Math.min(1, volume)) });
  },

  toggleMute: () => {
    set((state) => ({ isMuted: !state.isMuted }));
  },

  setIsPlaying: (isPlaying: boolean) => {
    set({ isPlaying });
  },

  setCurrentTime: (time: number) => {
    set({ currentTime: time });
  },

  setDuration: (duration: number) => {
    set({ duration });
  },

  setAudioUrl: (url: string | null) => {
    set({ audioUrl: url });
  },

  play: (file: AudioFile) => {
    // Stop current playback immediately when switching files
    const currentState = get();
    if (currentState.currentFile && currentState.currentFile.id !== file.id) {
      // Reset everything when switching to a different file
      set({ 
        isPlaying: false,
        currentTime: 0,
        audioUrl: null, // Clear URL to force reload
        duration: 0 // Reset duration
      });
    }
    
    // Set new file and start playing
    set({ 
      currentFile: file, 
      currentlyPlaying: file.id,
      isPlaying: true,
      currentTime: 0
    });
  },

  stop: () => {
    playbackEndedCallback = null;
    set({ 
      isPlaying: false, 
      currentTime: 0,
      currentFile: null,
      currentlyPlaying: null,
      audioUrl: null
    });
  },

  setOnPlaybackEnded: (cb: (() => void) | null) => {
    playbackEndedCallback = cb;
  },

  _notifyPlaybackEnded: () => {
    const cb = playbackEndedCallback;
    playbackEndedCallback = null;
    cb?.();
  },
}));













